/* Altara TRUST & SAFETY · DirectAxis email scam · slide asset (~15s)
 * Non-participating institution scenario.
 * Beat 1: platform=Email · Beat 2: email pastes in · Beat 3: PDF attaches ·
 * Beat 4: claim fields fill · Beat 5: Run Trust Check · Beat 6: HIGH RISK verdict. */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, MessageSquare, Link2, FileText, X, Paperclip, Loader2,
  AlertTriangle, Mail, FileWarning, Landmark, Info, Check,
  ExternalLink, Flag, ArrowRight, Play, RotateCcw, ChevronRight,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";

const EMAIL_BODY = `Good day Customers,

DirectAxis is giving out personal loan and business loan on a promotional offer during this period at a lower rate of 5% and your repayments are fixed for the full term of your loan.

Both personal loan and business loan offer open to blacklisted individuals and those under debt review.`;

const PLATFORMS = ["WhatsApp","SMS","Email","TikTok","Facebook","Instagram","Website","Phone call","Other"];

const FINDINGS = [
  { title: "Sender not from official domain",
    body: "The email address directfinancialconsultant@outlook.com is not an official DirectAxis domain.",
    Icon: Mail, tone: "amber" },
  { title: "Unusual offer",
    body: "A 5% fixed rate, open to blacklisted individuals and those under debt review, is highly unusual and inconsistent with responsible lending practices.",
    Icon: FileText, tone: "gold" },
  { title: "Suspicious attachment",
    body: "The attached document \u201CDirectAxis Special Offer.pdf\u201D could not be verified as an official DirectAxis document.",
    Icon: Paperclip, tone: "purple" },
  { title: "Institution not verified",
    body: "This communication does not match DirectAxis\u2019s official communication channels on record in Altara.",
    Icon: Landmark, tone: "blue" },
  { title: "Generic greeting",
    body: "\u201CGood day Customers\u201D is a common tactic in fraudulent messages.",
    Icon: Info, tone: "orange" },
];

export default function WalletAltaraTrustCheckScamDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") === "1";
  if (embed) {
    return (
      <div className="w-screen h-screen flex items-center justify-center px-6 py-6 overflow-hidden"
           style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}
           data-testid="altara-trust-check-scam-embed">
        <div className="w-full max-w-[1400px]">
          <EmbedHeader />
          <div className="mt-4"><TrustCheckStage autoplay={autoplay || true} /></div>
        </div>
        <SlideBrandMark />
      </div>
    );
  }
  return (
    <WalletShell activeTab="demos">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="altara-trust-check-scam">
        <Header />
        <DownloadStrip />
        <TrustCheckStage />
        <Footnote />
      </div>
    </WalletShell>
  );
}

function Header() {
  return (
    <header>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Demo Simulation</div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>
        Nomsa checks a DirectAxis loan email
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-3xl">
        Direct Axis isn&apos;t an Altara participating institution. Nomsa uploads
        the email + attachment. Altara analyses it against known-sender rules,
        offer plausibility, attachment provenance and Trusted Institutions on
        record — and returns a HIGH RISK verdict with named findings.
      </p>
    </header>
  );
}
function EmbedHeader() {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
        Altara Consumer Intelligence · Trust &amp; Safety
      </div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold mt-0.5" style={{ color: NAVY }}>
        Nomsa checks a suspicious DirectAxis email
      </h1>
      <div className="text-[13px] text-slate-600 mt-1">
        Upload · analyse · verdict — for institutions Altara doesn&apos;t participate in.
      </div>
    </div>
  );
}
function SlideBrandMark() {
  return (
    <div className="absolute bottom-3 right-5 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: NAVY, opacity: 0.55 }}>
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co
    </div>
  );
}
function DownloadStrip() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Slide assets</div>
        <div className="text-sm text-slate-700 mt-0.5">MP4 for modern PPT · GIF for universal embedding.</div>
      </div>
      <a href="/altara-trust-check-scam.mp4" download data-testid="dl-mp4"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white"
         style={{ background: NAVY }}>Download MP4 <ArrowRight className="w-3.5 h-3.5" /></a>
      <a href="/altara-trust-check-scam.gif" download data-testid="dl-gif"
         className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold border"
         style={{ background: "white", borderColor: NAVY, color: NAVY }}>
        Download GIF <ArrowRight className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}
function Footnote() {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[12.5px] text-slate-600 leading-relaxed">
      <strong className="text-slate-800">What Altara does:</strong> compare the evidence you upload against
      known-sender rules and your Trusted Institutions.
      <strong className="text-slate-800 ml-1">What Altara does not:</strong> block, delete, or report on your
      behalf. You decide the next move — Altara arms you with named findings and the official contact channels.
    </div>
  );
}

/* ═════════════ STAGE ═════════════════════ */
function TrustCheckStage({ autoplay = false }) {
  const [platform, setPlatform] = useState("");           // "" | "Email"
  const [typed, setTyped] = useState("");
  const [attached, setAttached] = useState(false);
  const [claimInst, setClaimInst] = useState("");
  const [claimPerson, setClaimPerson] = useState("");
  const [phase, setPhase] = useState("idle");             // idle | filling | running | results
  const [findingIdx, setFindingIdx] = useState(-1);       // -1..4
  const [showReco, setShowReco] = useState(false);
  const timers = useRef([]);
  const fired = useRef(false);
  const t = (ms, fn) => timers.current.push(setTimeout(fn, ms));
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const reset = () => {
    clear();
    setPlatform(""); setTyped(""); setAttached(false);
    setClaimInst(""); setClaimPerson("");
    setPhase("idle"); setFindingIdx(-1); setShowReco(false);
  };
  const play = () => {
    if (phase !== "idle" && phase !== "results") return;
    reset();
    setPhase("filling");
    // Beat 1 · platform (0 -> 0.9s)
    t(600, () => setPlatform("Email"));
    // Beat 2 · type email (1.0 -> 5.0s)
    const chars = EMAIL_BODY.length;
    const perChar = 3600 / chars;
    for (let i = 1; i <= chars; i++) t(1000 + i * perChar, () => setTyped(EMAIL_BODY.slice(0, i)));
    // Beat 3 · attach PDF (5.0s)
    t(5000, () => setAttached(true));
    // Beat 4 · claim fields (5.6 -> 6.6s)
    const inst = "DirectAxis";
    for (let i = 1; i <= inst.length; i++) t(5600 + i * 45, () => setClaimInst(inst.slice(0, i)));
    const person = "DirectAxis Team";
    for (let i = 1; i <= person.length; i++) t(6200 + i * 40, () => setClaimPerson(person.slice(0, i)));
    // Beat 5 · Run Trust Check (7.4s pulse, 7.9s click)
    t(7900, () => setPhase("running"));
    // Beat 6 · verdict (9.0s onwards)
    t(9000, () => { setPhase("results"); setFindingIdx(0); });
    t(9700,  () => setFindingIdx(1));
    t(10400, () => setFindingIdx(2));
    t(11100, () => setFindingIdx(3));
    t(11800, () => setFindingIdx(4));
    t(12800, () => setShowReco(true));
  };

  useEffect(() => {
    if (!autoplay || fired.current) return;
    fired.current = true;
    setTimeout(play, 700);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);
  useEffect(() => () => clear(), []);

  const btnPulsing = phase === "filling" && attached && claimPerson.length === "DirectAxis Team".length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4"
         data-testid="trust-check-stage">
      {/* LEFT · Trust Check form */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        {/* Navy header */}
        <div className="px-5 py-4 flex items-center gap-3" style={{ background: NAVY }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: GOLD }}>
            <Search className="w-5 h-5" style={{ color: NAVY }} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>Trust Check</div>
            <div className="font-display text-lg font-semibold text-white">Check something suspicious</div>
          </div>
        </div>
        <div className="p-5 space-y-5 flex-1">
          {/* Step 1 */}
          <div>
            <StepLabel n={1} label="What are you checking?" />
            <div className="grid grid-cols-3 gap-2 mt-2">
              <TypeCard Icon={MessageSquare} title="Message / Screenshot" sub="Paste the message you received." selected />
              <TypeCard Icon={Link2} title="Link / Website" sub="Paste any URL to check." />
              <TypeCard Icon={FileText} title="Document" sub="Upload a document." />
            </div>
          </div>
          {/* Step 2 */}
          <div>
            <StepLabel n={2} label="Where did it arrive?" />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PLATFORMS.map(p => {
                const on = platform === p;
                return (
                  <motion.div key={p} layout
                    animate={{
                      background: on ? NAVY : "white",
                      color: on ? "white" : "#475569",
                      borderColor: on ? NAVY : "#E2E8F0",
                    }}
                    className="px-3 py-1.5 rounded-full text-[11.5px] font-semibold border"
                    data-testid={`platform-${p}${on ? "-on" : ""}`}
                  >{p}</motion.div>
                );
              })}
            </div>
          </div>
          {/* Step 3 */}
          <div>
            <StepLabel n={3} label="The evidence" />
            <div className="mt-2 rounded-lg border p-3 min-h-[120px] max-h-[135px] overflow-hidden text-[12.5px] leading-relaxed whitespace-pre-line"
                 style={{ borderColor: "#E2E8F0", background: "white", color: "#334155" }}
                 data-testid="evidence-textarea">
              {typed}
              {phase === "filling" && typed.length < EMAIL_BODY.length && (
                <span className="inline-block w-[2px] h-[1em] align-text-bottom ml-0.5"
                      style={{ background: NAVY, animation: "cbl 0.8s steps(2) infinite" }} />
              )}
              {!typed && phase === "idle" && (
                <span className="text-slate-400">Paste the message…</span>
              )}
            </div>
            {/* Attachment */}
            <AnimatePresence>
              {attached && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            className="mt-2 rounded-lg border border-slate-200 p-2.5 flex items-center gap-2.5"
                            data-testid="attachment-pdf">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                       style={{ background: "#FEE2E2" }}>
                    <FileText className="w-4 h-4" style={{ color: "#DC2626" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold" style={{ color: NAVY }}>DirectAxis Special Offer.pdf</div>
                    <div className="text-[10.5px] text-slate-500">491 KB</div>
                  </div>
                  <X className="w-4 h-4 text-slate-400" />
                </motion.div>
              )}
            </AnimatePresence>
            <div className="mt-1.5 flex items-center gap-1 text-[11.5px] text-slate-500">
              <Paperclip className="w-3 h-3" /> Add another file (optional)
            </div>
          </div>
          {/* Step 4 */}
          <div>
            <StepLabel n={4} label="Who does it claim to be?" optional />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="rounded-lg border border-slate-200 px-3 py-2 text-[13px]" style={{ color: NAVY, background: "white" }}
                   data-testid="claim-institution">
                {claimInst || <span className="text-slate-400">Institution</span>}
              </div>
              <div className="rounded-lg border border-slate-200 px-3 py-2 text-[13px]" style={{ color: NAVY, background: "white" }}
                   data-testid="claim-person">
                {claimPerson || <span className="text-slate-400">Person</span>}
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="pt-1 flex items-center gap-2">
            <motion.button
              animate={{
                scale: btnPulsing ? [1, 1.04, 1] : phase === "running" ? 0.96 : 1,
                background: phase === "running" ? "#334155" : NAVY,
              }}
              transition={btnPulsing ? { duration: 0.9, repeat: 1, ease: "easeInOut" }
                                     : { duration: 0.2 }}
              data-testid="run-trust-check"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-[13px] font-semibold text-white"
              style={{ background: NAVY }}
            >
              {phase === "running"
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing…</>
                : <><Search className="w-4 h-4" /> Run Trust Check</>}
            </motion.button>
            <button onClick={reset} className="text-[12.5px] text-slate-500 px-3 py-2">Reset</button>
          </div>
        </div>
        <style>{`@keyframes cbl { 0%,50% {opacity:1} 51%,100% {opacity:0} }`}</style>
      </div>

      {/* RIGHT · Results */}
      <div className="rounded-2xl overflow-hidden">
        <AnimatePresence mode="wait">
          {phase !== "results" && (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="h-full min-h-[560px] rounded-2xl border border-slate-200 bg-white flex items-center justify-center p-6 text-center">
              <div>
                <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
                     style={{ background: `${GOLD}22` }}>
                  <Search className="w-6 h-6" style={{ color: NAVY }} />
                </div>
                <div className="mt-3 font-display text-[15px] font-semibold" style={{ color: NAVY }}>
                  Results appear here.
                </div>
                <div className="text-[12.5px] text-slate-500 mt-1 max-w-xs mx-auto">
                  Complete the four steps on the left and tap <strong>Run Trust Check</strong>.
                </div>
              </div>
            </motion.div>
          )}
          {phase === "results" && (
            <motion.div key="results" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 space-y-4"
                        data-testid="results-panel">
              {/* HIGH RISK banner */}
              <div className="rounded-xl p-4 flex items-start gap-3"
                   style={{ background: "#FEF2F2" }}
                   data-testid="high-risk-banner">
                <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ background: "#FCA5A5" }}>
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: "#B91C1C" }}>High risk</div>
                  <div className="font-display text-xl font-semibold mt-0.5" style={{ color: NAVY }}>This looks suspicious.</div>
                  <div className="text-[12.5px] text-slate-600 mt-1 leading-snug">
                    This message shows several warning signs and may not be from a legitimate DirectAxis communication.
                  </div>
                </div>
              </div>

              {/* Key findings */}
              <div>
                <div className="text-[12px] font-semibold" style={{ color: NAVY }}>Key findings</div>
                <div className="mt-2 space-y-1">
                  {FINDINGS.map((f, i) => (
                    <AnimatePresence key={i}>
                      {findingIdx >= i && (
                        <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="rounded-lg py-2 px-1 flex items-start gap-3"
                                    data-testid={`finding-${i}`}>
                          <FindingIcon Icon={f.Icon} tone={f.tone} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-semibold" style={{ color: NAVY }}>{f.title}</div>
                            <div className="text-[11.5px] text-slate-600 leading-snug mt-0.5">{f.body}</div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 mt-1" />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  ))}
                </div>
              </div>

              {/* Recommendation */}
              <AnimatePresence>
                {showReco && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.35 }}
                              className="rounded-xl p-3 flex items-start gap-3"
                              style={{ background: "#ECFDF5" }}
                              data-testid="recommendation">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                         style={{ background: "#059669" }}>
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <div className="text-[12px] font-semibold" style={{ color: "#065F46" }}>Our recommendation</div>
                      <div className="text-[12px] text-slate-700 leading-snug mt-0.5">
                        Do not respond, do not click on any links and do not open the attachment.
                        If you are unsure, contact DirectAxis using the official channels listed in your Trusted Institutions.
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Buttons */}
              <AnimatePresence>
                {showReco && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.3 }}
                              className="flex flex-wrap items-center gap-2 pt-1">
                    <button className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-[12.5px] font-semibold text-white"
                            style={{ background: NAVY }}>
                      <ExternalLink className="w-3.5 h-3.5" /> View official DirectAxis details
                    </button>
                    <button className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-[12.5px] font-semibold border"
                            style={{ background: "white", borderColor: "#E2E8F0", color: NAVY }}>
                      Report this message
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Controls (bottom, full width) */}
      <div className="lg:col-span-2 flex items-center justify-end gap-2">
        {phase !== "idle" && (
          <button onClick={reset} data-testid="reset-scam"
                  className="w-8 h-8 rounded-full flex items-center justify-center border"
                  style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={play} disabled={phase !== "idle" && phase !== "results"}
                data-testid="play-scam"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-60"
                style={{ background: NAVY }}>
          <Play className="w-3.5 h-3.5" /> {phase === "idle" ? "Play" : phase === "results" ? "Replay" : "Playing…"}
        </button>
      </div>
    </div>
  );
}

function StepLabel({ n, label, optional }) {
  return (
    <div className="text-[10px] font-semibold tracking-[0.18em] uppercase flex items-center gap-1.5"
         style={{ color: GOLD }}>
      <span className="text-slate-500 font-mono">{n}</span> · <span>{label}</span>
      {optional && <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>}
    </div>
  );
}

function TypeCard({ Icon, title, sub, selected }) {
  return (
    <div className="rounded-lg border p-2.5"
         style={{
           borderColor: selected ? NAVY : "#E2E8F0",
           background: selected ? "white" : "white",
           boxShadow: selected ? `0 0 0 2px ${NAVY}` : "none",
         }}>
      <Icon className="w-4 h-4" style={{ color: NAVY }} />
      <div className="text-[11.5px] font-semibold mt-1" style={{ color: NAVY }}>{title}</div>
      <div className="text-[10.5px] text-slate-500 leading-snug">{sub}</div>
    </div>
  );
}

function FindingIcon({ Icon, tone }) {
  const map = {
    amber:  { bg: "#FEF3C7", fg: "#B45309" },
    gold:   { bg: `${GOLD}22`, fg: NAVY },
    purple: { bg: "#EDE9FE", fg: "#6D28D9" },
    blue:   { bg: "#DBEAFE", fg: "#1D4ED8" },
    orange: { bg: "#FED7AA", fg: "#C2410C" },
  };
  const c = map[tone] || map.amber;
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
         style={{ background: c.bg }}>
      <Icon className="w-4 h-4" style={{ color: c.fg }} />
    </div>
  );
}
